import { useState } from 'react';
import { Button, Alert, Collapse } from 'reactstrap';
import { toast } from 'react-toastify';
import TournamentDetailCard from './TournamentDetailCard';
import { startHandsRest, requestRebalanceRest } from '@/services/tournaments/tournaments-service';
import { TournamentStatus } from '@repo/common-types';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';

interface DevOnlySectionProps {
  tournament: {
    id: string;
    url?: string | null;
    status: TournamentStatus;
  };
  onRefetch: () => void;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const DevOnlySection = ({ tournament, onRefetch }: DevOnlySectionProps) => {
  const [isStartingHands, setIsStartingHands] = useState(false);
  const [isRequestingRebalance, setIsRequestingRebalance] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleStartHands = async () => {
    if (!tournament.url) {
      toast.error('Tournament URL not available');
      return;
    }

    setIsStartingHands(true);
    try {
      await startHandsRest(tournament.url);
      toast.success('Start hands event sent to all tables');
      onRefetch();
    } catch (error) {
      console.error('Start hands error:', error);
      const apiError = error as ApiError;
      toast.error(apiError?.response?.data?.message || 'Failed to start hands');
    } finally {
      setIsStartingHands(false);
    }
  };

  const handleRequestRebalance = async () => {
    if (!tournament.url) {
      toast.error('Tournament URL not available');
      return;
    }

    setIsRequestingRebalance(true);
    try {
      await requestRebalanceRest(tournament.url);
      toast.success('Rebalance request sent successfully');
      onRefetch();
    } catch (error) {
      console.error('Rebalance request error:', error);
      const apiError = error as ApiError;
      toast.error(apiError?.response?.data?.message || 'Failed to request rebalance');
    } finally {
      setIsRequestingRebalance(false);
    }
  };

  const isPaused = tournament.status === TournamentStatus.PAUSED;

  return (
    <div className="w-100 my-3">
      <TournamentDetailCard title="Tournament Controls" className="w-100">
        <div className="d-flex justify-content-end mb-2">
          <Button
            color="link"
            className="text-decoration-none d-flex align-items-center gap-1 px-0"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? <IoChevronDown size={18} /> : <IoChevronForward size={18} />}
            {isExpanded ? 'Hide controls' : 'Show controls'}
          </Button>
        </div>

        <Collapse isOpen={isExpanded}>
          <Alert color="warning" className="mb-3">
            <strong>⚠️ Admin Only</strong> - These controls should be used carefully as they directly affect the
            tournament state.
          </Alert>

          <div className="d-flex flex-wrap gap-3">
            <Button
              color="warning"
              onClick={handleStartHands}
              disabled={isStartingHands || !tournament.url}
              className="d-flex align-items-center justify-content-center gap-2"
              style={{ minWidth: '150px' }}
            >
              {isStartingHands ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Starting...
                </>
              ) : (
                'Start Hands'
              )}
            </Button>

            <Button
              color="info"
              onClick={handleRequestRebalance}
              disabled={isRequestingRebalance || !tournament.url}
              className="d-flex align-items-center justify-content-center gap-2"
              style={{ minWidth: '180px' }}
            >
              {isRequestingRebalance ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Requesting...
                </>
              ) : (
                'Request Rebalance'
              )}
            </Button>
          </div>

          {!tournament.url && (
            <div className="mt-2">
              <small className="text-muted">
                Tournament URL is not available. These controls require an active tournament.
              </small>
            </div>
          )}

          <div className="mt-3">
            <small className="text-muted">
              <strong>Start Hands:</strong> Sends a start hand event to all tables in the tournament.
              <br />
              <strong>Request Rebalance:</strong> Triggers table rebalancing in case tables get stuck or unbalanced.
              <span className={isPaused ? 'text-success' : 'text-danger'}>
                {isPaused ? ' ✓ Tournament is paused' : ' ⚠️ Tournament must be paused'}
              </span>
            </small>
          </div>
        </Collapse>
      </TournamentDetailCard>
    </div>
  );
};

export default DevOnlySection;
